import api from "../api";
import type { TableTemplate } from "../models/ManualAuthoring";

type ApiResponse<T> = {
  statusCode: number;
  message?: string;
  data?: T | null;
  error?: string | null;
};

export type TableAlignment = "left" | "center" | "right";

export type TableTemplateRecord = {
  tableId: number;
  templateName: string;
  displayTitle: string;
  tableData: string;
  isDeleted: boolean;
  schemaVersion: number;
  width?: string | null;
  alignment?: TableAlignment | null;
  headerRepeat: boolean;
  keepTogether: boolean;
  isLocked: boolean;
  lockedBy?: string | null;
  lockedAt?: string | null;
  rowVersion?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
};

export type CreateTableTemplateDto = {
  templateName: string;
  displayTitle: string;
  tableData: string;
  schemaVersion?: number;
  width?: string | null;
  alignment?: TableAlignment;
  headerRepeat?: boolean;
  keepTogether?: boolean;
};

export type UpdateTableTemplateDto = {
  tableId: number;
  templateName: string;
  displayTitle: string;
  tableData: string;
  schemaVersion?: number;
  width?: string | null;
  alignment?: TableAlignment;
  headerRepeat?: boolean;
  keepTogether?: boolean;
  rowVersion: string;
};

export type TableTemplateLockState = {
  tableId: number;
  isLocked: boolean;
  lockedBy?: string | null;
  lockedAt?: string | null;
};

function isSuccess(statusCode?: number): boolean {
  return !!statusCode && statusCode >= 200 && statusCode < 300;
}

function toApiError<T>(
  response: ApiResponse<T> | undefined,
  fallbackMessage: string
): Error {
  return new Error(response?.error || response?.message || fallbackMessage);
}

function normalizeCreatePayload(payload: CreateTableTemplateDto): CreateTableTemplateDto {
  const schemaVersion = payload.schemaVersion ?? 0;
  return {
    ...payload,
    schemaVersion: schemaVersion > 0 ? schemaVersion : 1,
  };
}

export function dtoToSchema(dto: TableTemplateRecord): TableTemplate | null {
  return parseTableTemplateSchema(dto.tableData);
}

export function schemaToCreateDto(
  templateName: string,
  displayTitle: string,
  schema: TableTemplate
): CreateTableTemplateDto {
  return normalizeCreatePayload({
    templateName,
    displayTitle,
    tableData: stringifyTableTemplateSchema(schema),
    schemaVersion: schema.schemaVersion ?? 1,
    width: schema.width,
    alignment: schema.alignment,
    headerRepeat: schema.headerRepeat,
    keepTogether: schema.keepTogether,
  });
}

export async function fetchTableTemplates(oem: string): Promise<TableTemplateRecord[]> {
  const { data } = await api.get<ApiResponse<TableTemplateRecord[]>>(
    `/TableTemplates/${oem}`
  );

  if (!isSuccess(data?.statusCode)) {
    throw toApiError(data, "Failed to load table templates");
  }

  return Array.isArray(data.data) ? data.data : [];
}

export async function fetchTableTemplateById(
  oem: string,
  id: number
): Promise<TableTemplateRecord> {
  const { data } = await api.get<ApiResponse<TableTemplateRecord>>(
    `/TableTemplates/${oem}/${id}`
  );

  if (isSuccess(data?.statusCode) && data.data) return data.data;
  throw toApiError(data, "Failed to load table template");
}

export async function createTableTemplate(
  oem: string,
  payload: CreateTableTemplateDto
): Promise<TableTemplateRecord> {
  const { data } = await api.post<ApiResponse<TableTemplateRecord>>(
    `/TableTemplates/${oem}`,
    normalizeCreatePayload(payload)
  );

  if (isSuccess(data?.statusCode) && data.data) {
    return data.data;
  }

  throw toApiError(data, "Failed to create table template");
}

export async function updateTableTemplate(
  oem: string,
  payload: UpdateTableTemplateDto
): Promise<TableTemplateRecord> {
  const { data } = await api.put<ApiResponse<TableTemplateRecord>>(
    `/TableTemplates/${oem}`,
    payload
  );

  if (isSuccess(data?.statusCode) && data.data) {
    return data.data;
  }

  throw toApiError(data, "Failed to update table template");
}

export async function deleteTableTemplate(oem: string, id: number): Promise<void> {
  const { data } = await api.delete<ApiResponse<null>>(
    `/TableTemplates/${oem}/${id}`
  );

  if (!isSuccess(data?.statusCode)) {
    throw toApiError(data, "Failed to delete table template");
  }
}

export async function lockTableTemplate(
  oem: string,
  id: number
): Promise<TableTemplateLockState> {
  const { data } = await api.post<ApiResponse<TableTemplateLockState>>(
    `/TableTemplates/${oem}/${id}/lock`
  );

  if (isSuccess(data?.statusCode) && data.data) return data.data;

  throw toApiError(data, "Failed to lock table template");
}

export async function unlockTableTemplate(
  oem: string,
  id: number
): Promise<TableTemplateLockState> {
  const { data } = await api.post<ApiResponse<TableTemplateLockState>>(
    `/TableTemplates/${oem}/${id}/unlock`
  );

  if (isSuccess(data?.statusCode) && data.data) return data.data;

  throw toApiError(data, "Failed to unlock table template");
}

export function parseTableTemplateSchema(
  tableData: string | null | undefined
): TableTemplate | null {
  if (!tableData) return null;
  try {
    return JSON.parse(tableData) as TableTemplate;
  } catch {
    return null;
  }
}

export function stringifyTableTemplateSchema(schema: TableTemplate): string {
  return JSON.stringify(schema);
}
